import { useCallback, useMemo, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import type { GridApi, ModelUpdatedEvent, RowDoubleClickedEvent } from '@ag-grid-community/core';
import { endOfDay, format } from 'date-fns';
import { CalendarDays, FileSpreadsheet, Filter, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import CalendarRangePicker from '@/components/calendar-range-picker';
import type { ChipOption } from '@/components/chip-group';
import { ChipGroup, FacetLabel } from '@/components/chip-group';
import Container from '@/components/container';
import { CopyCountButton } from '@/components/copy-count-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MultiCombobox } from '@/components/ui/multi-combobox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useGrid } from '@/contexts/grid-context';
import { useRawChanges } from '@/hooks/use-raw-changes';
import type { RawChange } from '@/hooks/use-raw-changes';
import { getRegion, getRegionLabel } from '@/lib/region-utils';
import { autoFitColumns } from '@/lib/sheetjs-patches';
import { changeColumnDefs } from './components/change-grid-columns';
import ChangeSheet from './components/change-sheet';

const FACETS = ['tld', 'region', 'registrar'] as const;
type Facet = typeof FACETS[number];

function emptyFacetFilters(): Record<Facet, Set<string>> {
  return {
    tld: new Set(),
    region: new Set(),
    registrar: new Set(),
  };
}

const TLD_OPTIONS: ChipOption[] = [
  { value: 'bank', label: '.Bank' },
  { value: 'insurance', label: '.Insurance' },
];

const ROW_SELECTION = { mode: 'singleRow' } as const;

function getRowId(params: { data: RawChange }) {
  return params.data.id;
}

// getRegion only returns these four; the compound regions in REGIONS are for isInRegion.
const REGION_OPTIONS: ChipOption[] = (['US', 'EU-EEA', 'ASIA', 'OTHER'] as const).map((value) => ({
  value,
  label: getRegionLabel(value),
}));

function facetKeys({ domain, data }: RawChange): Record<Facet, string> {
  return {
    tld: domain.split('.').pop()?.toLowerCase() ?? '',
    region: getRegion(data.registrantIntCountryCode) ?? '',
    registrar: data.domainSponsorName ?? '',
  };
}

function RawChangesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>();
  const [facetFilters, setFacetFilters] = useState(emptyFacetFilters);
  const [selectedChange, setSelectedChange] = useState<RawChange | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [displayedRows, setDisplayedRows] = useState<RawChange[]>([]);
  const [hasColumnFilter, setHasColumnFilter] = useState(false);
  const gridApiRef = useRef<GridApi<RawChange> | null>(null);
  const { registerGridApi } = useGrid();

  const { data: changes, isLoading } = useRawChanges();
  const allChanges = useMemo(() => changes ?? [], [changes]);

  const isInDateRange = useMemo(() => {
    if (!dateRange?.from)
      return;
    const start = dateRange.from.getTime();
    const end = endOfDay(dateRange.to ?? dateRange.from).getTime();
    return ({ data }: RawChange) => {
      const updatedOn = Date.parse(data.updatedOn);
      return updatedOn >= start && updatedOn <= end;
    };
  }, [dateRange]);

  const keyedRows = useMemo(
    () => allChanges.map((row) => ({ row, keys: facetKeys(row) })),
    [allChanges],
  );

  // Options come from every row so a selected value never drops out of view.
  const facetOptions = useMemo(() => {
    const result = {} as Record<Facet, string[]>;
    for (const f of FACETS) {
      const counts = new Map<string, number>();
      for (const { keys } of keyedRows)
        counts.set(keys[f], (counts.get(keys[f]) ?? 0) + 1);
      result[f] = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value]) => value);
    }
    return result;
  }, [keyedRows]);

  const scopedRows = useMemo(
    () => (isInDateRange ? keyedRows.filter(({ row }) => isInDateRange(row)) : keyedRows),
    [keyedRows, isInDateRange],
  );

  const matchesFacets = useCallback(
    (keys: Record<Facet, string>, skip?: Facet) =>
      FACETS.every((f) => f === skip || facetFilters[f].size === 0 || facetFilters[f].has(keys[f])),
    [facetFilters],
  );

  const filteredRows = useMemo(
    () => scopedRows.filter(({ keys }) => matchesFacets(keys)).map(({ row }) => row),
    [scopedRows, matchesFacets],
  );

  // Each facet's counts apply the other facets' selections but not its own.
  const facetCounts = useMemo(() => {
    const result = {} as Record<Facet, Record<string, number>>;
    for (const f of FACETS) {
      const counts: Record<string, number> = {};
      for (const { keys } of scopedRows) {
        if (matchesFacets(keys, f))
          counts[keys[f]] = (counts[keys[f]] ?? 0) + 1;
      }
      result[f] = counts;
    }
    return result;
  }, [scopedRows, matchesFacets]);

  const toggleFacet = useCallback((key: Facet, value: string) => {
    setFacetFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value))
        next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });
  }, []);

  const earliestDate = useMemo(() => {
    let earliest = Number.POSITIVE_INFINITY;
    for (const { data } of allChanges) {
      const updatedOn = Date.parse(data.updatedOn);
      if (!Number.isNaN(updatedOn))
        earliest = Math.min(earliest, updatedOn);
    }
    return Number.isFinite(earliest) ? new Date(earliest) : undefined;
  }, [allChanges]);

  const registrarOptions = useMemo<ChipOption[]>(
    () => facetOptions.registrar.map((value) => ({
      value,
      label: `${value || '(Blank)'} (${(facetCounts.registrar[value] ?? 0).toLocaleString()})`,
    })),
    [facetOptions, facetCounts],
  );

  const handleRowDoubleClick = (event: RowDoubleClickedEvent<RawChange>) => {
    if (event.data) {
      setSelectedChange(event.data);
      setIsSheetOpen(true);
    }
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setSelectedChange(null);
    }
  };

  const handleGridReady = (params: { api: GridApi<RawChange> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  // Mirror what the grid shows (date range + chips + search + column filters, in
  // sort order) so the count and export always match the visible rows.
  const handleModelUpdated = useCallback((event: ModelUpdatedEvent<RawChange>) => {
    const rows: RawChange[] = [];
    event.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data)
        rows.push(node.data);
    });
    setDisplayedRows(rows);
    setHasColumnFilter(event.api.isColumnFilterPresent());
  }, []);

  const hasActiveFilters = FACETS.some((f) => facetFilters[f].size > 0)
    || dateRange !== undefined
    || searchQuery.trim() !== ''
    || hasColumnFilter;

  const clearFilters = () => {
    setFacetFilters(emptyFacetFilters());
    setDateRange(undefined);
    setSearchQuery('');
    gridApiRef.current?.setFilterModel(null);
  };

  const handleExport = useCallback(() => {
    if (displayedRows.length === 0) {
      toast.error('No contact changes to export');
      return;
    }

    try {
      const exportRows = displayedRows.map(({ domain, data }) => {
        const updatedOn = Date.parse(data.updatedOn);
        return {
          'Updated On': Number.isNaN(updatedOn) ? '' : format(new Date(updatedOn), 'MM/dd/yyyy h:mm a'),
          'Domain': domain,
          'Registrant Name': data.registrantIntName ?? '',
          'Organization': data.registrantIntOrganization ?? '',
          'Email': data.registrantEmail ?? '',
          'Phone': data.registrantPhone ?? '',
          'Phone Ext': data.registrantPhoneExt ?? '',
          'Fax': data.registrantFax ?? '',
          'Fax Ext': data.registrantFaxExt ?? '',
          'Street 1': data.registrantIntAddressStreet1 ?? '',
          'Street 2': data.registrantIntAddressStreet2 ?? '',
          'Street 3': data.registrantIntAddressStreet3 ?? '',
          'City': data.registrantIntCity ?? '',
          'State': data.registrantIntState ?? '',
          'Postal Code': data.registrantIntPostCode ?? '',
          'Country': data.registrantIntCountryCode ?? '',
          'Registrant Name (Loc)': data.registrantLocName ?? '',
          'Organization (Loc)': data.registrantLocOrganization ?? '',
          'Street 1 (Loc)': data.registrantLocAddressStreet1 ?? '',
          'Street 2 (Loc)': data.registrantLocAddressStreet2 ?? '',
          'Street 3 (Loc)': data.registrantLocAddressStreet3 ?? '',
          'City (Loc)': data.registrantLocCity ?? '',
          'State (Loc)': data.registrantLocState ?? '',
          'Postal Code (Loc)': data.registrantLocPostCode ?? '',
          'Country (Loc)': data.registrantLocCountryCode ?? '',
          'Registrar': data.domainSponsorName ?? '',
          'IANA ID': data.domainSponsorIanaId ?? '',
          'Domain Created': data.domainCreatedOn ?? '',
          'Domain Expires': data.domainExpireOn ?? '',
          'Domain Updated': data.domainUpdatedOn ?? '',
          'Contact ROID': data.contactRoid ?? '',
          'Domain ROID': data.domainRoid ?? '',
          'Server Transaction ID': data.serverTransactionId ?? '',
        };
      });

      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);
      ws['!cols'] = autoFitColumns(exportRows);
      utils.book_append_sheet(wb, ws, 'Contact Changes');

      const rangeLabel = dateRange?.from
        ? `${format(dateRange.from, 'yyyyMMdd')}_${format(dateRange.to ?? dateRange.from, 'yyyyMMdd')}`
        : 'all_time';
      const filename = `raw_changes_${rangeLabel}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
      writeFile(wb, filename);
      toast.success(`Exported ${exportRows.length.toLocaleString()} contact changes to ${filename}`);
    } catch {
      toast.error('Failed to export contact changes');
    }
  }, [displayedRows, dateRange]);

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="mt-2 text-xl font-bold">Raw Contact Changes</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isLoading || displayedRows.length === 0}
          className="gap-2"
        >
          <FileSpreadsheet className="size-4" />
          Export
          {' '}
          {displayedRows.length.toLocaleString()}
          {' '}
          changes
        </Button>
      </div>

      {isLoading
        ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading contact changes...</span>
                </div>
              </CardContent>
            </Card>
          )
        : (
            <>
              <Card className="mb-4 border-dashed">
                <CardContent className="py-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                      <ChipGroup
                        label="TLD"
                        options={TLD_OPTIONS}
                        selected={facetFilters.tld}
                        onToggle={(v) => toggleFacet('tld', v)}
                        counts={facetCounts.tld}
                      />
                      <ChipGroup
                        label="Region"
                        options={REGION_OPTIONS}
                        selected={facetFilters.region}
                        onToggle={(v) => toggleFacet('region', v)}
                        counts={facetCounts.region}
                      />
                    </div>

                    <div className="flex flex-wrap gap-x-8 gap-y-4 border-t pt-3">
                      <div className="flex flex-col gap-1.5">
                        <FacetLabel>Registrar</FacetLabel>
                        <div className="w-72">
                          <MultiCombobox
                            options={registrarOptions}
                            selected={[...facetFilters.registrar]}
                            onChange={(values) => setFacetFilters((prev) => ({ ...prev, registrar: new Set(values) }))}
                            placeholder="Select registrars..."
                            emptyText="No registrars found."
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <FacetLabel>Date Range</FacetLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 gap-2 text-xs font-normal">
                              <CalendarDays className="size-3.5" />
                              {dateRange?.from
                                ? `${format(dateRange.from, 'MMM d, yyyy')} – ${format(dateRange.to ?? dateRange.from, 'MMM d, yyyy')}`
                                : 'All dates'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarRangePicker
                              value={dateRange ?? { from: undefined }}
                              onValueChange={(range) => setDateRange(range?.from ? range : undefined)}
                              fromDate={earliestDate}
                              captionLayout="dropdown"
                              className="border-0"
                            />
                            <div className="border-t p-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDateRange(undefined)}
                                disabled={!dateRange}
                                className="w-full text-xs"
                              >
                                Clear dates
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t pt-3">
                      <div className="flex items-center gap-2">
                        {hasActiveFilters
                          ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearFilters}
                                className="gap-1.5 text-xs text-muted-foreground"
                              >
                                <RotateCcw className="size-3" />
                                Clear filters
                              </Button>
                            )
                          : (
                              <span className="text-xs text-muted-foreground">
                                Click a chip or pick a date range to filter, or search by domain, registrant, or email
                              </span>
                            )}
                      </div>

                      <div className="flex items-center gap-3">
                        <CopyCountButton count={displayedRows.length} label="changes" />

                        <div className="relative w-56">
                          <Input
                            type="search"
                            placeholder="Search domains, registrants, emails..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8 pl-8 text-xs"
                          />
                          <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <AGDataGrid
                columnDefs={changeColumnDefs}
                rowData={filteredRows}
                quickFilterText={searchQuery}
                getRowId={getRowId}
                rowSelection={ROW_SELECTION}
                onRowDoubleClicked={handleRowDoubleClick}
                onGridReady={handleGridReady}
                onModelUpdated={handleModelUpdated}
                overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No contact changes match the selected filters</span>'
              />
            </>
          )}

      <ChangeSheet
        open={isSheetOpen}
        onOpenChange={handleSheetOpenChange}
        change={selectedChange || undefined}
      />
    </Container>
  );
}

export default RawChangesPage;
